/**
 * Boot-time SSO bootstrap decision (redesign of a T024 architectural defect; see `main.tsx`'s own
 * doc comment for the full citation/rationale and `AuthenticationRouter.tsx`'s
 * `AuthenticationSsoHandoff` for the other call site sharing the same client).
 *
 * `oidc-spa`'s redirect URI is always this app's root — never a dedicated callback route
 * (`node_modules/oidc-spa/README.md` line 67; `createOidc.js`'s hardcoded `homeUrlAndRedirectUri`)
 * — so the only place a real callback response is ever actually observed is app boot. This module
 * is deliberately extracted out of `main.tsx` (a plain entry point with eager CSS/module side
 * effects that this repository does not unit-test anywhere) so the actual decision logic — does
 * this page load need to show a boot-time recovery screen instead of the app? — has real,
 * dependency-injected unit coverage; `main.tsx` itself stays a thin, untested wiring layer that
 * delegates to {@link runSsoBoot} and renders based on its result.
 */

import { IdTokenValidationError } from './token-validation';
import { markSignInLanding } from './return-destination';
import { awaitCallbackSession } from './callback-outcome';
import { bucketAuthenticationOutcomeDuration } from './duration-bucket';
import type { AuthenticationOutcomeEvent } from './events';
import type { OidcClientDependencies, SsoOidcClient } from './oidc-client';

/**
 * - `'ready'`: either the rollout gate is inactive (no SSO processing at all), or the shared
 *   client resolved with nothing left to react to at boot (already logged in, or a fresh visit
 *   with no session and no rejected callback) — mount the app as normal.
 * - `'boot_failed'`: the shared client's own construction (discovery/network) rejected outright.
 * - `'callback_rejected'`: the URL that greeted this page load actually carried an
 *   authorization-response-shaped parameter (see {@link hasAuthorizationResponseParams}), but the
 *   resolved client reports no session — `oidc-spa` rejected a forged/expired/replayed callback
 *   (FR-033), and this must not be treated as an ordinary anonymous visit.
 */
export type SsoBootOutcome = 'ready' | 'boot_failed' | 'callback_rejected';

/**
 * Authorization-response query/fragment parameter names `oidc-spa`'s own `oidcEarlyInit()`
 * recognizes when landing back at this app's root after a real provider redirect (see
 * `node_modules/oidc-spa/src/core/earlyInit.ts`'s `handleOidcCallback()`). This list has no
 * security role of its own — `oidc-spa`'s state/PKCE validation inside `createOidc()` is the
 * actual security control — it exists purely so {@link stripAuthorizationResponseParams} can
 * scrub every recognized name from the visible URL, regardless of which of them a given response
 * actually carries. Deliberately broader than {@link LOGIN_RESPONSE_PARAM_NAMES} below — see that
 * constant's own doc comment for why classification needs a narrower list than stripping does.
 */
export const AUTHORIZATION_RESPONSE_PARAM_NAMES = [
  'code',
  'state',
  'error',
  'error_description',
  'error_uri',
  'session_state',
  'iss',
] as const;

/**
 * The subset of {@link AUTHORIZATION_RESPONSE_PARAM_NAMES} that can only appear on a genuine
 * OAuth **authorization** (login) response — never on `oidc-spa`'s own post-**logout** redirect.
 *
 * `state` is deliberately excluded here despite being present on every real login response: as
 * confirmed directly from `oidc-spa`'s own source (`node_modules/oidc-spa/src/core/StateData.ts`),
 * a logout redirect's `state` value is generated by the exact same `generateStateUrlParamValue()`
 * used for login, and is indistinguishable from a login `state` value by shape alone (both are a
 * fixed 32-character `b2lkYy1zcGEu`-prefixed random string) — `oidc-spa` disambiguates the two
 * only via a `StateData.Redirect.action: "login" | "logout"` field it stores internally, which
 * this module cannot see. Treating bare `state` presence as "a login response landed here" — the
 * bug this comment replaces — misclassifies an ordinary, successful logout redirect (which also
 * carries `state`, for `oidc-spa`'s own unrelated bookkeeping) as a *rejected sign-in attempt*,
 * surfacing `AuthenticationRecovery`'s `callback_invalid` copy ("This sign-in attempt is no longer
 * valid") to a user who was simply signed out. `code` (present on every successful authorization
 * response) and the `error`/`error_description`/`error_uri` family (present on every provider-
 * rejected one) are never emitted on a logout redirect, so they remain reliable, unambiguous
 * signals that a real login response — accepted or rejected — is what landed here.
 */
const LOGIN_RESPONSE_PARAM_NAMES = ['code', 'error', 'error_description', 'error_uri'] as const;

/**
 * True when `paramString` (a `location.search` or `location.hash`, with or without its leading
 * `?`/`#`) carries a parameter that can only appear on a genuine login (authorization) response —
 * see {@link LOGIN_RESPONSE_PARAM_NAMES}. Must be evaluated by the caller BEFORE `oidcEarlyInit()`
 * runs — it synchronously strips the full {@link AUTHORIZATION_RESPONSE_PARAM_NAMES} set via
 * `history.replaceState` as part of resolving `shouldLoadApp`, so the URL no longer carries them
 * afterward for a response shape it recognizes as its own (see the caveat on
 * {@link stripAuthorizationResponseParams}).
 */
export function hasAuthorizationResponseParams(paramString: string): boolean {
  const params = new URLSearchParams(paramString.replace(/^[?#]/, ''));
  return LOGIN_RESPONSE_PARAM_NAMES.some((name) => params.has(name));
}

/** The OIDC Core §3.1.2.6 errors a `prompt=none` request returns when the user must sign in again. */
const SILENT_SIGN_IN_REQUIRED_ERRORS = [
  'login_required',
  'interaction_required',
  'consent_required',
  'account_selection_required',
] as const;

/**
 * True when `paramString` is the provider's "not signed in" answer to a silent session restore
 * (`prompt=none`), for example after `oidc-spa` finds a "logged in" record whose provider session has
 * ended. That is a signed-out user, not a rejected sign-in, so boot must not show the
 * `callback_invalid` recovery screen for it (#4193 FR-008).
 */
export function isSilentSignInRequiredResponse(paramString: string): boolean {
  const error = new URLSearchParams(paramString.replace(/^[?#]/, '')).get('error');
  return error !== null && (SILENT_SIGN_IN_REQUIRED_ERRORS as readonly string[]).includes(error);
}

/**
 * Removes every recognized authorization-response parameter from `paramString` (a `location.search`
 * or `location.hash`, with or without its leading `?`/`#`), returning the remainder with a leading
 * `?`/`#` restored only if anything is left — an empty result is `''`, never a bare separator.
 *
 * Defense-in-depth atop `oidcEarlyInit()`'s own stripping (`node_modules/oidc-spa/src/core/
 * earlyInit.ts`'s `handleOidcCallback()`): that only fires for a `state` value shaped exactly like
 * one `oidc-spa` itself generates (`StateData.ts`'s `getIsStatQueryParamValue` — a fixed 32-
 * character value with a specific prefix). A hostile or malformed `state` that does not match that
 * shape at all (as opposed to one that matches the shape but was never actually issued/stored) is
 * invisible to that check, so `oidc-spa` leaves the URL untouched — this function is what actually
 * guarantees "the visible URL never retains an authorization-response parameter after settling"
 * (FR-033) for that case. Applied by `main.tsx` unconditionally, once, right after `oidcEarlyInit()`
 * — safe to run even when `oidcEarlyInit()` already rewrote the URL to something clean (no
 * recognized parameters left to remove is a no-op) or to the original pre-login destination.
 */
export function stripAuthorizationResponseParams(paramString: string): string {
  const separator = paramString.startsWith('#') ? '#' : '?';
  const params = new URLSearchParams(paramString.replace(/^[?#]/, ''));
  for (const name of AUTHORIZATION_RESPONSE_PARAM_NAMES) {
    params.delete(name);
  }
  const serialized = params.toString();
  return serialized.length > 0 ? `${separator}${serialized}` : '';
}

/** What boot does with SSO on this page load; see {@link planSsoBoot}. */
export interface SsoBootPlan {
  /** A sign-in response to complete or reject at boot (FR-009); not the silent-restore "sign-in required" answer. */
  readonly hadAuthorizationResponseOnLoad: boolean;
  /** Whether boot starts the SSO client; read when boot decides, so a control answer that lands first counts. */
  readonly shouldStartSso: () => boolean;
  /**
   * Whether `main.tsx` strips authorization-response parameters from the address bar: whenever one
   * was on load. A sign-in response always starts SSO, and the silent-restore answer is cleaned too.
   */
  readonly shouldSanitizeUrl: boolean;
}

/**
 * Decides, from the URL as it was on load, whether boot handles SSO (#4193 FR-008, FR-009): the
 * rollout gate is on, OR a sign-in response came back in the URL, OR this browser already holds an
 * SSO session record. Existing sessions and returning sign-ins therefore never depend on the rollout
 * control, and a page with none of the three starts no SSO, exactly like the gate-off path.
 *
 * The provider's "sign-in required" answer to a silent restore ({@link isSilentSignInRequiredResponse})
 * is cleaned from the URL but is not a sign-in to complete: the user is simply signed out. A page
 * without an authorization response keeps its URL (Constitution IX). `search` and `hash` must be read
 * before `oidcEarlyInit()` rewrites them.
 */
export function planSsoBoot(input: {
  readonly search: string;
  readonly hash: string;
  readonly isRolloutActive: () => boolean;
  readonly hasSsoSessionRecord: () => boolean;
}): SsoBootPlan {
  const authorizationResponse =
    hasAuthorizationResponseParams(input.search) || hasAuthorizationResponseParams(input.hash);
  const silentSignInEnded =
    isSilentSignInRequiredResponse(input.search) || isSilentSignInRequiredResponse(input.hash);
  const hadAuthorizationResponseOnLoad = authorizationResponse && !silentSignInEnded;

  return {
    hadAuthorizationResponseOnLoad,
    shouldStartSso: () => input.isRolloutActive() || hadAuthorizationResponseOnLoad || input.hasSsoSessionRecord(),
    shouldSanitizeUrl: authorizationResponse,
  };
}

export interface RunSsoBootDependencies {
  /**
   * Whether boot starts the SSO client: `main.tsx` passes {@link planSsoBoot}'s `shouldStartSso`
   * (#4193 FR-008, FR-009), so existing sessions and returning sign-ins never depend on the rollout
   * control.
   */
  readonly shouldStartSso: () => boolean;
  /** Called once when boot starts the SSO client; `main.tsx` passes `rollout.ts`'s `markSsoPathActive`. */
  readonly onSsoStart?: () => void;
  /** Defaults call sites to `oidc-client.ts`'s `getSharedSsoOidcClient`. */
  readonly getSsoOidcClient: (dependencies: OidcClientDependencies) => Promise<SsoOidcClient>;
  /** Lazily resolves the boundary — only invoked when boot starts SSO, so a page with no SSO reason
   * never constructs `auth-boundary.ts`'s app-wide singleton either (FR-035). */
  readonly getAuthBoundary: () => OidcClientDependencies['authBoundary'];
  /** Captured by the caller before `oidcEarlyInit()` mutates `window.location` — see {@link hasAuthorizationResponseParams}. */
  readonly hadAuthorizationResponseOnLoad: boolean;
  /** Reports the callback outcome (FR-025); `main.tsx` passes `events.ts`'s `reportAuthenticationOutcome`. */
  readonly reportOutcome?: (event: AuthenticationOutcomeEvent) => void;
  /** Monotonic clock for the SC-002 duration bucket; defaults to `performance.now`. */
  readonly now?: () => number;
}

/** Reports the terminal callback event with its SC-002 duration bucket. */
function reportCallbackOutcome(
  dependencies: RunSsoBootDependencies,
  startedAt: number,
  outcome: SsoBootOutcome,
  error?: unknown,
): void {
  if (!dependencies.hadAuthorizationResponseOnLoad || !dependencies.reportOutcome) {
    return;
  }
  const elapsedMs = (dependencies.now ?? (() => performance.now()))() - startedAt;
  // `duration-bucket.ts` has no imports, so this does not load the analytics/store graph at boot.
  const durationBucket = bucketAuthenticationOutcomeDuration(elapsedMs);
  if (outcome === 'ready') {
    // The Backend session isn't established yet: the first `fetchSession` result reports it (T105).
    awaitCallbackSession(startedAt, dependencies.reportOutcome, dependencies.now);
    return;
  }
  const rejectedReason = error instanceof IdTokenValidationError ? 'mismatch' : 'malformed';
  const reason = outcome === 'boot_failed' ? 'unavailable' : rejectedReason;
  dependencies.reportOutcome({ stage: 'callback', outcome: 'failed', reason, durationBucket });
}

/** Resolves the boot-time SSO outcome. See `main.tsx` for how each outcome is rendered. */
export async function runSsoBoot(dependencies: RunSsoBootDependencies): Promise<SsoBootOutcome> {
  if (!dependencies.shouldStartSso()) {
    return 'ready';
  }
  dependencies.onSsoStart?.();

  const startedAt = (dependencies.now ?? (() => performance.now()))();
  let client: SsoOidcClient;
  try {
    client = await dependencies.getSsoOidcClient({ authBoundary: dependencies.getAuthBoundary() });
  } catch (error) {
    if (!dependencies.hadAuthorizationResponseOnLoad && !(error instanceof IdTokenValidationError)) {
      // No callback to finish, so an unavailable provider must not lock out legacy users or
      // existing legacy sessions (FR-019, FR-020; T106). The app loads; an SSO decision then retries
      // the client in the handoff and shows the unavailable screen if it still fails.
      return 'ready';
    }
    // A rejected ID token (T062) is a rejected callback, not an unavailable provider.
    const failed = error instanceof IdTokenValidationError ? 'callback_rejected' : 'boot_failed';
    reportCallbackOutcome(dependencies, startedAt, failed, error);
    return failed;
  }

  const outcome: SsoBootOutcome =
    dependencies.hadAuthorizationResponseOnLoad && !client.isUserLoggedIn ? 'callback_rejected' : 'ready';
  if (outcome === 'ready' && dependencies.hadAuthorizationResponseOnLoad) {
    // oidc-spa has already restored the pre-login destination into the address bar (FR-024, T086).
    markSignInLanding(window.location.pathname);
  }
  reportCallbackOutcome(dependencies, startedAt, outcome);
  return outcome;
}
